import {
	addDays as addDaysDateTime,
	addTime,
	calculateDayDifference,
	getStartOfDay,
	millisecondsInAMinute,
	millisecondsInAnHour
} from "client/DateAndTime/dateTimeUtils";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import { convertToTimeOnly } from "client/DateAndTime/timeUtils";
import { TimeZone, getUtcTimeZone } from "client/DateAndTime/timezones";
import { OnlineMeeting } from "client/Api/Notifications/notifications";
import { Rect } from "client/Misc/commonTypes";
import { calendarLogic } from "client_legacy/Workspace/LiveInformation/Calendar/calendarLogic";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import { formatDate } from "client_legacy/ndaw/dateFormatting";
import { CalendarAppointment } from "../../CalendarPage";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateOnly, convertToDateTime } from "client/DateAndTime/dateUtils";
import { AppointmentResponseStatus, CalendarColor } from "client/Api/Calendar/calendar";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";

export type RegularEventToRender = {
	kind: "regular";
	id: string | null;
	subject: string;
	start: Date;
	end: Date;
	hasAttachments: boolean;
	isRecurring: boolean;
	isPrivate: boolean;
	meeting: OnlineMeeting | null;
	isCancelled: boolean;
	position: Rect;
	color: CalendarColor | null;
	locations: readonly string[] | null;
	isAllDayEvent: boolean;
	myResponseStatus: AppointmentResponseStatus | null;
	isOrganizer: boolean;
	canEdit: boolean;
	calendarId: string;
};

export type PlaceholderEventToRender = {
	kind: "placeholder";
	start: Date;
	end: Date;
	isAllDayEvent: boolean;
	position: Rect;
	color: CalendarColor | null;
};

export type EventToRender = ObjectWithClientId<RegularEventToRender | PlaceholderEventToRender>;

type DayWithAssociatedAppointmentsInternal = {
	day: DateOnly;
	start: number;
	end: number;
	appointments: CalendarAppointment[];
};

export type DayWithAssociatedAppointments = {
	day: DateOnly;
	appointments: CalendarAppointment[];
};

export function listAppointmentsAssociatedWithDay(
	allAppointments: readonly CalendarAppointment[],
	dateRange: readonly DateOnly[],
	timezone: TimeZone
): readonly DayWithAssociatedAppointments[] {
	const result: DayWithAssociatedAppointmentsInternal[] = [];

	for (const day of dateRange) {
		const startOfDay: Date = convertToDateTime(day, timezone);
		const endOfDay: Date = addDaysDateTime(
			startOfDay,
			1,
			timezone
		);

		const currentDay: DateOnly = convertToDateOnly(
			startOfDay,
			timezone
		);

		result.push({
			day: currentDay,
			start: startOfDay.getTime(),
			end: endOfDay.getTime(),
			appointments: []
		});
	}

	const sortedAppointments: readonly CalendarAppointment[] = sortBy(
		allAppointments,
		(x) => x.start
	);

	let startDayIndex: number = 0;
	for (const appointment of sortedAppointments) {
		const startTime: number = appointment.start
			.getTime();

		const endTime: number = appointment.end
			.getTime();

		for (let i = startDayIndex; i < result.length; i++) {
			const day: DayWithAssociatedAppointmentsInternal = result[i];

			if (startTime >= day.end) {
				startDayIndex++;
			} else if (startTime < day.end && endTime >= day.start) {
				day.appointments.push(appointment);
			}
		}

		if (startDayIndex >= result.length) {
			break;
		}
	}

	return result.map((x) => ({
		day: x.day,
		appointments: x.appointments
	}));
}

export function getEventsForDay(
	allAppointments: readonly CalendarAppointment[],
	day: Date,
	timezone: TimeZone
): readonly EventToRender[] {
	const startOfDay: Date = getStartOfDay(day, timezone);

	const endOfDay: Date = addDaysDateTime(
		startOfDay,
		1,
		timezone
	);

	const dayInfo: TimeRange = {
		start: startOfDay.getTime(),
		end: endOfDay.getTime()
	};

	const appointmentsForDay: readonly CalendarAppointment[] = getAppointmentsForDay(
		allAppointments,
		dayInfo,
		timezone
	);

	if (appointmentsForDay.length === 0) {
		return [];
	}

	const conflictGroups: CalendarAppointment[][] = getConflictGroups(
		appointmentsForDay
	);

	const events: EventToRender[] = [];

	for (const group of conflictGroups) {
		const {
			eventsWithPosition,
			numberOfDefinedColumns
		} = packEvents(
			group,
			dayInfo,
			timezone
		);

		for (const event of eventsWithPosition) {
			events.push(
				convertToEventToRender(
					event,
					numberOfDefinedColumns
				)
			);
		}
	}

	return events;
}

function getAppointmentsForDay(
	allAppointments: readonly CalendarAppointment[],
	day: TimeRange,
	timezone: TimeZone
): readonly CalendarAppointment[] {
	return allAppointments
		.filter(
			appointment => {
				const {
					startOrEndsOnDay,
					isADayOrLonger
				} = getAppointmentSpanFlags(
					appointment,
					day,
					timezone
				);

				return startOrEndsOnDay
					&& !isADayOrLonger;
			}
		);
}

type SpanFlags = {
	startOrEndsOnDay: boolean;
	isADayOrLonger: boolean;
	startsBeforeDay: boolean;
	endsAfterDay: boolean;
};

function getAppointmentSpanFlags(
	appointment: CalendarAppointment,
	day: TimeRange,
	timezone: TimeZone
): SpanFlags {
	const {
		startDateToCompare,
		endDateToCompare
	} = calendarLogic.getAppointmentSpanInfo(appointment, timezone);

	const startTime: number = startDateToCompare.getTime();
	const endTime: number = endDateToCompare.getTime();

	const startOrEndsOnDay: boolean = startTime >= day.start && startTime < day.end
		|| endTime > day.start && endTime <= day.end;

	const endsAfterDay: boolean = endTime > day.end;
	const startsBeforeDay: boolean = startTime < day.start;

	const isADayOrLonger: boolean = appointment.isAllDayEvent
		|| calculateDayDifference(
			appointment.end,
			startDateToCompare,
			timezone,
			true
		) >= 1;

	return {
		isADayOrLonger,
		startOrEndsOnDay,
		endsAfterDay,
		startsBeforeDay
	};
}

function convertToEventToRender(
	eventWithPosition: EventWithPosition,
	numberOfDefinedColumns: number
): EventToRender {
	const {
		event,
		start,
		end,
		column,
		width
	} = eventWithPosition;

	const columnWidth: number = 100 / numberOfDefinedColumns;
	const top: number = (start * 100) / MINUTES_IN_DAY;
	const bottom: number = 100 - ((end * 100) / MINUTES_IN_DAY);
	const left: number = column * columnWidth;
	const right: number = 100 - (left + (width * columnWidth));

	const position: Rect = {
		top,
		bottom,
		right,
		left
	};

	if (event.kind === "regular") {
		return {
			...event,
			position,
			subject: event.subject
				? event.subject
				: localize("_Busy"),
		};
	}

	return {
		...event,
		position
	};
}

export type EventWithPosition = {
	event: CalendarAppointment;
	column: number;
	width: number;
	start: number;
	end: number;
};

export type PackEventsResult = {
	eventsWithPosition: readonly EventWithPosition[];
	numberOfDefinedColumns: number;
};

export function packEvents(
	appointments: readonly CalendarAppointment[],
	day: TimeRange,
	timezone: TimeZone
): PackEventsResult {
	const events: CalendarAppointment[] = sortBy(
		appointments,
		(x) => x.start.getTime()
	);

	const result: EventWithPosition[] = [];

	for (const event of events) {
		const eventWithPosition: EventWithPosition = calculatePosition(
			event,
			result,
			day,
			timezone
		);

		result.push(eventWithPosition);
	}

	if (result.length === 0) {
		return {
			eventsWithPosition: [],
			numberOfDefinedColumns: 0
		};
	}

	const numberOfDefinedColumns = Math.max(...result.map(x => x.column)) + 1;

	for (const placedEvent of result) {
		const eventsWithConflicts = result.filter(
			e => getClientId(e.event) !== getClientId(placedEvent.event)
				&& doDatesConflict(e.event, placedEvent.event)
		);

		const occupiedColumns = new Set(eventsWithConflicts.map(e => e.column));

		for (let i = placedEvent.column + 1; i < numberOfDefinedColumns; i++) {
			if (!occupiedColumns.has(i)) {
				placedEvent.width++;
			} else {
				break;
			}
		}
	}

	return {
		eventsWithPosition: result,
		numberOfDefinedColumns
	};
}

function calculatePosition(
	event: CalendarAppointment,
	alreadyPlacedEvents: readonly EventWithPosition[],
	day: TimeRange,
	timezone: TimeZone
): EventWithPosition {
	const numberOfDefinedColumns: number = Math.max(...alreadyPlacedEvents.map(x => x.column), 0) + 1;
	const eventsWithConflicts: EventWithPosition[] = alreadyPlacedEvents.filter(e => doDatesConflict(e.event, event));
	const occupiedColumns: Set<number> = new Set(eventsWithConflicts.map(e => e.column));

	const {
		startsBeforeDay,
		endsAfterDay
	} = getAppointmentSpanFlags(
		event,
		day,
		timezone
	);

	const {
		start,
		end
	} = getStartEnd(
		{
			start: event.start,
			end: event.end,
			day,
			startsBeforeDay,
			endsAfterDay
		},
		timezone
	);

	for (let i = 0; i < numberOfDefinedColumns; i++) {
		if (!occupiedColumns.has(i)) {
			return {
				event,
				column: i,
				width: 1,
				start,
				end
			};
		}
	}

	return {
		event,
		column: numberOfDefinedColumns,
		width: 1,
		start,
		end
	};
}

function doDatesConflict<TAppointment extends {start: Date; end: Date;}>(
	event1: TAppointment,
	event2: TAppointment
): boolean {
	const event1StartTime: number = event1.start
		.getTime();
	const event1EndTime: number = event1.end
		.getTime();
	const event2StartTime: number = event2.start
		.getTime();
	const event2EndTime: number = event2.end
		.getTime();

	const hasConflict: boolean = event1StartTime < event2EndTime && event1EndTime > event2StartTime
		|| event2StartTime < event1EndTime && event2EndTime > event1StartTime;

	return hasConflict;
}

type EventStartEnd = {
	start: number;
	end: number;
};

type GetStartEndOptions = {
	start: Date;
	end: Date;
	day: TimeRange;
	startsBeforeDay: boolean;
	endsAfterDay: boolean;
};

function getStartEnd(
	options: GetStartEndOptions,
	timezone: TimeZone
): EventStartEnd {
	const {
		day,
		endsAfterDay,
		startsBeforeDay
	} = options;

	const isTimeShiftDay: boolean = (day.end - day.start) !== HOURS_IN_DAY * millisecondsInAnHour;

	let start: number;
	let end: number;

	if (!isTimeShiftDay) {
		if (startsBeforeDay) {
			start = 0;
		} else {
			start = (options.start.getTime() - day.start) / millisecondsInAMinute;
		}

		if (endsAfterDay) {
			end = MINUTES_IN_DAY;
		} else {
			end = (options.end.getTime() - day.start) / millisecondsInAMinute;
		}
	} else {
		const startTime: TimeOnly = convertToTimeOnly(
			options.start,
			timezone
		);

		const endTime: TimeOnly = convertToTimeOnly(
			options.end,
			timezone
		);

		const startHour: number = startTime.hour;
		const startMinutes: number = startTime.minute;

		const endHour: number = endTime.hour;
		const endMinutes: number = endTime.minute;

		const isEndEndOfTheDay: boolean = endHour + endMinutes === 0
			&& day.end === options.end.getTime();

		start = startsBeforeDay
			? 0
			: (startHour * MINUTES_IN_HOUR) + startMinutes;

		end = endsAfterDay || isEndEndOfTheDay
			? MINUTES_IN_DAY
			: (endHour * MINUTES_IN_HOUR) + endMinutes;
	}

	return {
		start,
		end
	};
}

export function getConflictGroups<TAppointment extends {start: Date; end: Date;}>(
	appointments: readonly TAppointment[]
): TAppointment[][] {
	if (appointments.length === 0) {
		return [];
	}

	const events: readonly TAppointment[] = sortBy(
		appointments,
		x => x.start.getTime()
	);

	const conflictGroups: TAppointment[][] = [];
	let currentGroup: TAppointment[] = [];

	for (const event of events) {
		const hasConflict = currentGroup.some(
			x => doDatesConflict(x, event)
		);

		if (hasConflict) {
			currentGroup.push(event);
		} else {
			if (currentGroup.length > 0) {
				conflictGroups.push(currentGroup);
			}
			currentGroup = [event];
		}
	}

	if (currentGroup.length > 0) {
		conflictGroups.push(currentGroup);
	}

	return conflictGroups;
}

export type DayHour = {
	formattedTime: string;
	hour: number;
};

export function getDayHourRange(): readonly DayHour[] {
	const currentLocale: string = getCurrentCulture();
	const utcTimezone: TimeZone = getUtcTimeZone(currentLocale);
	const startOfDay: Date = getStartOfDay(
		new Date(),
		utcTimezone
	);

	const dayHours: DayHour[] = [];

	for (let i = 0; i < HOURS_IN_DAY; i++) {
		const date: Date = addTime(
			startOfDay,
			i,
			"hour"
		);

		const formattedTime: string = formatDate(
			date,
			"T",
			utcTimezone
		);

		dayHours.push({
			formattedTime,
			hour: i
		});
	}

	return dayHours;
};

const MINUTES_IN_HOUR: number = 60;
const HOURS_IN_DAY: number = 24;
const MINUTES_IN_DAY: number = HOURS_IN_DAY * MINUTES_IN_HOUR;

export type TimeRange = {
	start: number;
	// End should be considered exclusive (i.e. it is the start of the next day)
	end: number;
};
